import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import IMerchProduct from '../../../../../../models/restful/IMerchProduct';
import IMerchProductOrderItem from '../../../../../../models/restful/IMerchProductOrderItem';
import { useQuery } from '@tanstack/react-query';
import { apiV2 } from 'api/ApiV2';
import IMerchColor from 'models/restful/IMerchColor';
import IMerchSize from 'models/restful/IMerchSize';
import IMerchModel from 'models/restful/IMerchModel';

interface ProductVariationSelectorProps {
  product: IMerchProduct;
  orderItem: IMerchProductOrderItem;
  showRequired: boolean;
  onVariationChange: (productJson: string) => void;
}

interface SelectedVariation {
  ColorID?: string;
  SizeID?: string;
  ModelID?: string;
  Title?: string;
  colorTitle?: string;
  sizeTitle?: string;
  modelTitle?: string;
  changed?: boolean;
}

type VariationSelectorProps = {
  value?: string;
  options: Array<{
    ID: string | number;
    Title: string;
  }>;
  label: string;
  onChange: (value: string) => void;
  showRequired?: boolean;
};

const VariationSelector = ({
  value,
  label,
  options,
  onChange,
  showRequired
}: VariationSelectorProps) => {
  if (options.length <= 1) return null;

  return (
    <Col xs={12} lg={4} className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Select
        className={`form-select ${showRequired && !value ? 'is-invalid' : ''}`}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        required={true}
      >
        <option value="">Select {label.toLowerCase()}...</option>
        {options.map(option => (
          <option value={String(option.ID)} key={String(option.ID)}>
            {option.Title}
          </option>
        ))}
      </Form.Select>
      {showRequired && !value && (
        <div className="invalid-feedback">
          Please select a {label.toLowerCase()}
        </div>
      )}
    </Col>
  );
};

const ProductVariationSelector: React.FC<ProductVariationSelectorProps> = ({
  product,
  orderItem,
  showRequired,
  onVariationChange
}) => {
  const { Colors = [], Sizes = [], Models = [] } = product;
  const [selectedVariation, setSelectedVariation] = useState<SelectedVariation>(
    {}
  );

  const {
    data: { colors, models, sizes } = { colors: [], models: [], sizes: [] }
  } = useQuery({
    queryKey: ['shop-data'],
    queryFn: () => apiV2.category.getShopData(),
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    const { ColorID, SizeID, ModelID, Title, changed } = selectedVariation;
    if (ColorID && SizeID && ModelID && (changed || !orderItem.ProductJSON)) {
      onVariationChange(JSON.stringify({ ColorID, SizeID, ModelID, Title }));
    }
  }, [selectedVariation]);

  // If there's only one option for any variation, automatically select it
  useEffect(() => {
    const newVariation: SelectedVariation = {
      ColorID: Colors.length === 1 ? String(Colors[0]) : '',
      SizeID: Sizes.length === 1 ? String(Sizes[0]) : '',
      ModelID: Models.length === 1 ? String(Models[0]) : '',
      Title: ''
    };

    if (orderItem.ProductJSON) {
      try {
        const parsed = JSON.parse(orderItem.ProductJSON);
        newVariation.ColorID = parsed.ColorID;
        newVariation.SizeID = parsed.SizeID;
        newVariation.ModelID = parsed.ModelID;
        newVariation.Title = parsed.Title;
      } catch (e) {
        console.error('Failed to parse ProductJSON:', e);
      }
    }

    const color = colors.find(c => String(c.ID) === newVariation.ColorID);
    const size = sizes.find(s => String(s.ID) === newVariation.SizeID);
    const model = models.find(m => String(m.ID) === newVariation.ModelID);

    newVariation.Title = `${color?.Title}, ${size?.Title}, ${model?.Title}`;
    newVariation.colorTitle = color?.Title;
    newVariation.sizeTitle = size?.Title;
    newVariation.modelTitle = model?.Title;

    setSelectedVariation(newVariation);
  }, [colors, models, sizes]);

  const handleVariationChange = (
    type: 'ColorID' | 'SizeID' | 'ModelID',
    value: string
  ) => {
    const newVariation = { ...selectedVariation } as SelectedVariation;
    newVariation[type] = value;
    // Find the corresponding titles
    const color = colors.find(c => String(c.ID) === newVariation.ColorID);
    const size = sizes.find(s => String(s.ID) === newVariation.SizeID);
    const model = models.find(m => String(m.ID) === newVariation.ModelID);

    newVariation.Title = `${color?.Title}, ${size?.Title}, ${model?.Title}`;
    newVariation.colorTitle = color?.Title;
    newVariation.sizeTitle = size?.Title;
    newVariation.modelTitle = model?.Title;
    newVariation.changed = true;
    setSelectedVariation(newVariation);
  };

  const hasSelectableOptions =
    Colors.length > 1 || Sizes.length > 1 || Models.length > 1;

  return (
    <div className="variation-selector">
      {hasSelectableOptions && (
        <h4 className="variation-selector__title">Choose a variation</h4>
      )}
      {!hasSelectableOptions && (
        <div className="variation-selector__description mb-3 d-flex align-items-center gap-3">
          <span className="fw-bold">Selected variation:</span>
          <div className="d-flex gap-2">
            {selectedVariation.colorTitle && (
              <span className="d-flex align-items-center">
                <span className="text-muted me-1">Color:</span>
                <span>{selectedVariation.colorTitle}</span>
              </span>
            )}
            {selectedVariation.sizeTitle && (
              <span className="d-flex align-items-center">
                <span className="text-muted me-1">Size:</span>
                <span>{selectedVariation.sizeTitle}</span>
              </span>
            )}
            {selectedVariation.modelTitle && (
              <span className="d-flex align-items-center">
                <span className="text-muted me-1">Model:</span>
                <span>{selectedVariation.modelTitle}</span>
              </span>
            )}
          </div>
        </div>
      )}
      <Row>
        <VariationSelector
          value={selectedVariation['ColorID']}
          label="Color"
          options={(Colors as unknown as IMerchColor['ID'][]).map(c => ({
            ID: c,
            Title: colors.find(color => color.ID === c)?.Title || ''
          }))}
          showRequired={showRequired}
          onChange={val => handleVariationChange('ColorID', val)}
        />
        <VariationSelector
          value={selectedVariation['SizeID']}
          label="Size"
          options={(Sizes as unknown as IMerchSize['ID'][]).map(s => ({
            ID: s,
            Title: sizes.find(size => size.ID === s)?.Title || ''
          }))}
          showRequired={showRequired}
          onChange={val => handleVariationChange('SizeID', val)}
        />
        <VariationSelector
          value={selectedVariation['ModelID']}
          label="Model"
          options={(Models as unknown as IMerchModel['ID'][]).map(m => ({
            ID: m,
            Title: models.find(model => model.ID === m)?.Title || ''
          }))}
          showRequired={showRequired}
          onChange={val => handleVariationChange('ModelID', val)}
        />
      </Row>
    </div>
  );
};

export default ProductVariationSelector;
